import React, { useState } from "react";
import {
  TableCell,
  TableRow,
  IconButton,
  Collapse,
  Box,
  Checkbox,
  TextField,
  Button,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Undo as UndoIcon,
  Comment as CommentIcon,
} from "@mui/icons-material";
import moment from "moment";
import { formatNumber } from "../utils/helpers";
import { guardarObservacion } from "../services/observaciones.service";

const FilaExpandible = ({
  item,
  isCoincidencia,
  isSoloDB,
  isSoloMP,
  expanded,
  onToggle,
  showCheckbox,
  isSelected,
  onSelect,
  isDisabled,
  sucursalesML,
  onDesconciliar,
  empresa,
  observaciones,
  onObservacionGuardada
}) => {
  const dbData = isCoincidencia ? item.db : isSoloDB ? item : null;
  const mpData = isCoincidencia ? item.mp : isSoloMP ? item : null;
  const totalColumns = showCheckbox ? 7 : 6;

  const observacionExistente = (observaciones || []).find(
    (o) =>
      (dbData?.id && o.id_pago_combinado === dbData.id) ||
      (mpData?.id && o.mp_payment_id === String(mpData.id))
  );

  const [textoObservacion, setTextoObservacion] = useState(
    observacionExistente?.observacion || ""
  );
  const [editandoObservacion, setEditandoObservacion] = useState(false);
  const [guardandoObservacion, setGuardandoObservacion] = useState(false);

  const tipoObservacion = isCoincidencia
    ? "coincidencia"
    : isSoloDB
      ? "solo_db"
      : "solo_mp";

  const handleGuardarObservacion = async () => {
    if (!textoObservacion.trim()) return;
    setGuardandoObservacion(true);
    try {
      await guardarObservacion({
        empresa,
        id_pago_combinado: dbData?.id || null,
        mp_payment_id: mpData?.id ? String(mpData.id) : null,
        referencia: mpData?.external_reference || dbData?.detalle || "",
        tipo: tipoObservacion,
        observacion: textoObservacion.trim(),
      });
      setEditandoObservacion(false);
      if (onObservacionGuardada) onObservacionGuardada();
    } catch (error) {
      alert(error.message || "Error al guardar la observación");
    } finally {
      setGuardandoObservacion(false);
    }
  };

  const isConciliado = dbData?.conciliado === 1;
  const checkboxChecked = isConciliado || isSelected || false;
  const checkboxDisabled = isConciliado || isDisabled;

  const sucursalPago = sucursalesML.find(sucursal => sucursal.mp_store_id === mpData?.store_id);

  return (
    <>
      <TableRow hover>
        {showCheckbox && (
          <TableCell sx={{ p: 1, width: isConciliado ? 76 : 40 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                size="small"
                checked={ checkboxChecked }
                onChange={(e) => onSelect(e.target.checked)}
                disabled={isDisabled}
              />
              {isConciliado && (
                <IconButton
                  size="small"
                  onClick={() => onDesconciliar(dbData)}
                  sx={{ color: "#ed6c02" }}
                  title="Desconciliar"
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </TableCell>
        )}
        <TableCell sx={{ fontSize: "0.8rem" }}>
          {mpData?.external_reference || dbData?.detalle || "-"}
        </TableCell>
        <TableCell sx={{ fontSize: "0.8rem" }}>
          {dbData?.fecha
            ? moment(dbData.fecha).format("DD-MM-YYYY HH:mm")
            : mpData?.date_approved
              ? moment(mpData.date_approved).format("DD-MM-YYYY HH:mm")
              : "-"}
        </TableCell>
        <TableCell align="right" sx={{ fontSize: "0.8rem" }}>
          {dbData?.monto ? `$${formatNumber(dbData.monto)}` : "-"}
        </TableCell>
        <TableCell align="right" sx={{ fontSize: "0.8rem" }}>
          {mpData?.transaction_amount
            ? `$${formatNumber(mpData.transaction_amount)}`
            : "-"}
        </TableCell>
        <TableCell align="right" sx={{ fontSize: "0.8rem" }}>
          {mpData?.recibido ? `$${formatNumber(mpData.recibido)}` : "-"}
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            {observacionExistente && (
              <CommentIcon
                fontSize="small"
                sx={{ color: "#1976d2", mr: 0.5 }}
                titleAccess={observacionExistente.observacion}
              />
            )}
            <IconButton size="small" onClick={onToggle} sx={{ color: "#757575" }}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={totalColumns} sx={{ py: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                p: 1.5,
                bgcolor: "#fafafa",
                borderTop: "1px solid #e8e8e8",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ mt: 0.5, minWidth: 30 }}>
                  {isCoincidencia &&
                    (item.coincide ? (
                      <CheckCircleIcon
                        sx={{ color: "#2e7d32", fontSize: 20 }}
                      />
                    ) : (
                      <WarningIcon sx={{ color: "#ed6c02", fontSize: 20 }} />
                    ))}
                  {isSoloDB && (
                    <InfoIcon sx={{ color: "#ed6c02", fontSize: 20 }} />
                  )}
                  {isSoloMP && (
                    <ErrorIcon sx={{ color: "#d32f2f", fontSize: 20 }} />
                  )}
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "2px 16px",
                    flex: 1,
                    fontSize: "0.8rem",
                  }}
                >
                  <Box>
                    {dbData?.pedido && (
                      <Box sx={{ display: "flex", gap: 0.5, py: 0.25 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#424242",
                            minWidth: 65,
                          }}
                        >
                          Pedido:
                        </span>
                        <span>{dbData.pedido}</span>
                      </Box>
                    )}
                    {dbData?.cliente && (
                      <Box sx={{ display: "flex", gap: 0.5, py: 0.25 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#424242",
                            minWidth: 65,
                          }}
                        >
                          Cliente:
                        </span>
                        <span>{dbData.cliente}</span>
                      </Box>
                    )}
                    {dbData?.chofer && (
                      <Box sx={{ display: "flex", gap: 0.5, py: 0.25 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#424242",
                            minWidth: 65,
                          }}
                        >
                          Chofer:
                        </span>
                        <span>{dbData.chofer}</span>
                      </Box>
                    )}
                    {dbData?.detalle && (
                      <Box sx={{ display: "flex", gap: 0.5, py: 0.25 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#424242",
                            minWidth: 65,
                          }}
                        >
                          Detalle:
                        </span>
                        <span>{dbData.detalle}</span>
                      </Box>
                    )}
                    {dbData?.conciliado !== undefined && (
                      <Box sx={{ display: "flex", gap: 0.5, py: 0.25 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#424242",
                            minWidth: 65,
                          }}
                        >
                          Conciliado:
                        </span>
                        <span
                          style={{
                            color:
                              dbData?.conciliado === 1 ? "#2e7d32" : "#d32f2f",
                          }}
                        >
                          {dbData?.conciliado === 1 ? "✅ Sí" : "❌ No"}
                        </span>
                      </Box>
                    )}
                  </Box>

                  <Box>
                    {mpData?.status && (
                      <Box sx={{ display: "flex", gap: 0.5, py: 0.25 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#424242",
                            minWidth: 65,
                          }}
                        >
                          Estado:
                        </span>
                        <span>{mpData.status}</span>
                      </Box>
                    )}
                    {mpData?.id && (
                      <Box sx={{ display: "flex", gap: 0.5, py: 0.25 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#424242",
                            minWidth: 65,
                          }}
                        >
                          ID MP:
                        </span>
                        <span>{mpData.id}</span>
                      </Box>
                    )}
                    {mpData?.payer?.identification?.number && (
                      <Box sx={{ display: "flex", gap: 0.5, py: 0.25 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#424242",
                            minWidth: 65,
                          }}
                        >
                          DNI/CUIT:
                        </span>
                        <span>{mpData.payer.identification.number}</span>
                      </Box>
                    )}
                    {mpData?.payer?.email && (
                      <Box sx={{ display: "flex", gap: 0.5, py: 0.25 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#424242",
                            minWidth: 65,
                          }}
                        >
                          Email:
                        </span>
                        <span>{mpData.payer.email}</span>
                      </Box>
                    )}
                    {mpData?.date_approved && (
                      <Box sx={{ display: "flex", gap: 0.5, py: 0.25 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#424242",
                            minWidth: 65,
                          }}
                        >
                          Aprobado:
                        </span>
                        <span>
                          {moment(mpData.date_approved).format(
                            "DD-MM-YYYY HH:mm",
                          )}
                        </span>
                      </Box>
                    )}
                    {sucursalPago && (
                      <Box sx={{ display: "flex", gap: 0.5, py: 0.25 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#424242",
                            minWidth: 65,
                          }}
                        >
                          Sucursal:
                        </span>
                        <span>
                          {sucursalPago.nombre}
                        </span>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #e8e8e8" }}>
                {editandoObservacion ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      size="small"
                      multiline
                      minRows={2}
                      fullWidth
                      placeholder="Escribí una observación sobre esta diferencia..."
                      value={textoObservacion}
                      onChange={(e) => setTextoObservacion(e.target.value)}
                      disabled={guardandoObservacion}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleGuardarObservacion}
                        disabled={guardandoObservacion || !textoObservacion.trim()}
                      >
                        Guardar
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setEditandoObservacion(false);
                          setTextoObservacion(observacionExistente?.observacion || "");
                        }}
                        disabled={guardandoObservacion}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  </Box>
                ) : observacionExistente ? (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <CommentIcon fontSize="small" sx={{ color: "#1976d2", mt: 0.3 }} />
                    <Box sx={{ flex: 1 }}>
                      <span style={{ fontSize: "0.8rem" }}>{observacionExistente.observacion}</span>
                      <Box sx={{ fontSize: "0.7rem", color: "#757575", mt: 0.25 }}>
                        {observacionExistente.usuario} ·{" "}
                        {moment(observacionExistente.updated_at || observacionExistente.created_at).format(
                          "DD-MM-YYYY HH:mm",
                        )}
                      </Box>
                    </Box>
                    <Button size="small" onClick={() => setEditandoObservacion(true)}>
                      Editar
                    </Button>
                  </Box>
                ) : (
                  <Button
                    size="small"
                    startIcon={<CommentIcon fontSize="small" />}
                    onClick={() => setEditandoObservacion(true)}
                  >
                    Agregar observación
                  </Button>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default FilaExpandible;
